import { useRouter } from "next/router";

import { useCallback, useEffect, useRef, useState } from "react";

import { twMerge } from "tailwind-merge";

import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";

import FontAwesomeIcon from "components/FontAwesomeIcon";

import styles from "./useScrollable.module.scss";

export const useScrollable = () => {
  const router = useRouter();

  const scrollableRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(false);
  const scroll = useCallback(
    (direction) => {
      const scrollable = scrollableRef.current;
      const scrollLeft = scrollable?.scrollLeft || 0;
      const offset = (direction === "right" ? 1 : -1) * Math.round(width / 2);
      scrollable?.scroll({ left: scrollLeft + offset, behavior: "smooth" });
    },
    [width]
  );

  const scrollToTab = useCallback(
    (tab) => {
      const scrollable = scrollableRef.current;

      const scrollLeft = tab.offsetLeft + tab.offsetWidth / 2 - width / 2;

      scrollable?.scroll({
        left: scrollLeft,
        behavior: "smooth",
      });
    },
    [width]
  );

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      try {
        let tabElement = event.currentTarget;
        if (
          tabElement.parentElement &&
          tabElement.parentElement !== scrollableRef.current
        ) {
          tabElement = tabElement.parentElement;
        }
        scrollToTab(tabElement);
      } catch {}
    },
    [scrollToTab]
  );

  const updateNavButtons = useCallback(() => {
    const scrollable = scrollableRef.current;

    if ((scrollable?.scrollLeft || 0) === 0) {
      setShowLeftNav(false);
    } else {
      setShowLeftNav(true);
    }
    if (
      Math.round(
        (scrollable?.scrollLeft || 0) + (scrollable?.clientWidth || 0)
      ) >= (scrollable?.scrollWidth || 0)
    ) {
      setShowRightNav(false);
    } else {
      setShowRightNav(true);
    }
  }, []);

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (!!ref && router.query.tab === tab) {
        activeTab.current = ref;
      }
      if (scrollableRef.current) {
        setScrollWidth(scrollableRef.current.scrollWidth);
        setWidth(scrollableRef.current.clientWidth);
        updateNavButtons();
      }
    },
    [router.query.tab, updateNavButtons]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  useEffect(() => {
    const scrollable = scrollableRef.current;

    updateNavButtons();

    scrollable?.addEventListener("scroll", updateNavButtons, { passive: true });

    return () => {
      scrollable?.removeEventListener("scroll", updateNavButtons);
    };
  }, [scrollWidth, updateNavButtons, width]);

  return {
    scrollableRef,
    showLeftNav,
    showRightNav,
    scroll,
    scrollToTab,
    onTabClick,
    activeTab,
    onTabLoad,
    LeftNav: ({ className }: { className: string }) =>
      showLeftNav ? (
        <button
          title="scroll left"
          className={twMerge(styles.tabsNav, styles.left, className)}
          onClick={() => scroll("left")}
        >
          <FontAwesomeIcon icon={faAngleDoubleLeft} />
        </button>
      ) : null,
    RightNav: ({ className }: { className: string }) =>
      showRightNav ? (
        <button
          title="scroll right"
          className={twMerge(styles.tabsNav, styles.right, className)}
          onClick={() => scroll("right")}
        >
          <FontAwesomeIcon icon={faAngleDoubleRight} />
        </button>
      ) : null,
  };
};
